/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - European Union 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [headlines.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 30.08.18 13:17
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import LightboxGallery from '../lightbox-gallery';
import {asyncFetchSpaceMediaByTypePaged} from '../../../actions/spaces';
import Widget from "../../widgets/widget";
import {getImageServerMediumUrl, getImageServerSmallUrl} from "../../../actions/environment";
import {useAuthorization, useGalleryChanges, useGenericSpaces, useHomeData} from "../../../selectors";
import SmoothScrollbar from "../../scrollbars/smooth-scrollbar";
import {formatFns, useLanguage} from "../../configuration/configuration";
import {Add, Cake, FormatQuote, MyLocation} from "@material-ui/icons";
import {GalleryIcon, UserPenIcon} from "../../navigation/icons/nav-icons";
import {RoundedButton, Tooltip} from "../../navigation/buttons/nav-buttons";
import UpdateStatusPopover from "../../dialogs/user/update-status-popover";
import {Popover} from "../../navigation/popover/popover";
import {PENDING_STATUS, SUCCESS_STATUS} from "../../../actions";
import {useHistory} from "react-router-dom";
import {NavigationGroup, NavigationRow} from "../../navigation/layout/nav-layout";
import useFetchGallery from "../../hooks/use-fetch-gallery";
import CreateGalleryPopover from "../../popover/create-gallery-popover";
import GalleryEntrySpace from "../../billboard-sidebar/lists/gallery-entry-space";


const HeadlinesHome = ({username, spaceId}) => {
    const [showMediaGallery, setShowMediaGallery] = useState({isOpen: false, index: 0});
    const [status, setStatus] = useState(PENDING_STATUS);
    const mediaGalleryRef = useRef(null);
    const mediaUrls = useRef([]);
    const {isAuthorized, authorization, isTransitioning} = useAuthorization();
    const {userdata, user, isOwner} = useHomeData();
    const {media, widgets} = useGenericSpaces();
    const {fetchGallerySpaces, spaces: gallerySpaces, status: galleryStatus} = useFetchGallery();
    const dispatch = useDispatch();
    const history = useHistory();
    const {spaceId: galleryId} = useGalleryChanges();
    const Lang = useLanguage();

    const fetchMedia = useCallback((username, spaceId) => {
        setStatus(PENDING_STATUS);
        // dispatch(asyncFetchSpaceMedia(username, spaceId, data => {
        dispatch(asyncFetchSpaceMediaByTypePaged(username, spaceId,
            'PICTURE', 0, 12, data => {
            mediaUrls.current = [];
            setStatus(SUCCESS_STATUS);
        }));
    }, []);

    useEffect(() => {
        fetchMedia(username, spaceId);
        fetchGallerySpaces(username);
    }, [username, spaceId, galleryId]);

    const handleShowMediaGallery = (isOpen, index) => {
        setShowMediaGallery({isOpen: isOpen, index: index})
    }

    const renderPictures = (media) => {
        return media.filter(entry => entry.type === 'PICTURE')
            .map((entry, idx) => {
                const url = getImageServerMediumUrl(entry.url);
                mediaUrls.current.push(url);

                return (<div key={idx} className="card">
                    <img className="card-img-top" src={url}
                         onClick={() => handleShowMediaGallery(true, idx)}/>
                </div>)
            })
    }

    const renderTopWidgets = (widgets) => {
        if (!widgets) return null;
        return widgets.filter(widget => widget.pos === 'LTOP')
            .map(widget => {
                return <Widget key={widget.id} widget={widget} authorization={authorization} mode='LEFT'/>
            })
    }

    const renderBottomWidgets = (widgets) => {
        if (!widgets) return null;
        return widgets.filter(widget => widget.pos === 'LBOTTOM')
            .map(widget => {
                return <Widget key={widget.id} widget={widget} authorization={authorization} mode='LEFT'/>
            })
    }

    const renderGallerySpaces = () => {
;        const galleries = gallerySpaces
        .map(gallery => {
            const description = gallery.description ? gallery.description.substr(0,64) : null;

            return <li key={gallery.spaceId} className='list-unstyled mt-1 mb-2'>
                <GalleryEntrySpace space={gallery} size='medium' text={description}/>
            </li>
        })
        return <ul className='list-unstyled'>{galleries}</ul>
    }

    // close is a callback from popover component
    const handleStatusPopover = ({close}) => {
        return <div>
            <UpdateStatusPopover user={user} onClose={close}/>
        </div>
    }

    // close is a callback from popover component
    // creates a gallery for a home spce (default)
    const handleGalleryPopover = ({close}) => {
        return <div>
            <CreateGalleryPopover onClose={close}/>
        </div>
    }

    const handleOnShowGallery = event => {
        event.preventDefault();
        history.push(`/${username}/gallery/home`);
    }


    if (status === PENDING_STATUS || isTransitioning) return null;
    const birthday = formatFns(new Date(userdata.birthday), 'd MMMM');

    return (
        <div className='headlines-container'>
            <div className='generic-navigation-information'>
                <div>
                    <FormatQuote className="mr-2 clr-blue"/>
                    <span className='generic-user-quote-text'>{userdata.status}</span>
                </div>

                {isAuthorized && isOwner && <div className='d-flex justify-content-end'>
                    <Tooltip title={Lang.user.updateStatus}>
                        <Popover className='popover-status-container'
                                 placement='bottom'
                                 render={handleStatusPopover}>
                            <button className='btn rounded-button-regular rounded-button btn-outline-light'>
                                <UserPenIcon className='font-user-pen'/>
                            </button>
                        </Popover>
                    </Tooltip></div>}
            </div>

            <div className='generic-navigation-information mb-2 rounded'>
                <div><MyLocation className="mr-2"/>
                    {Lang.user.currentLocation(user.firstname, userdata.address.city)}
                </div>
                <div><Cake className="mr-2"/>{Lang.user.hasBirthdayOn(birthday)}</div>
            </div>

            <div className="widget-container">
                {renderTopWidgets(widgets, authorization)}
            </div>

            <NavigationRow className='bg-white rounded'>
                <NavigationGroup>
                    <span className='sidebar-space-title ml-2'>{Lang.gallery.headline.billboard}</span>
                </NavigationGroup>
                <NavigationGroup>
                    <RoundedButton title={Lang.gallery.headline.tooltip}
                                   onClick={handleOnShowGallery}>
                        <GalleryIcon/>
                    </RoundedButton>
                </NavigationGroup>
            </NavigationRow>


            <SmoothScrollbar className='generic-navigation-information pictures-container rounded'>
                <div className='card-columns'>
                    {renderPictures(media)}
                </div>
            </SmoothScrollbar>

            <div className='generic-navigation-information mt-2 rounded'>
                <NavigationRow>
                    <NavigationGroup>
                        <span className='sidebar-space-title'>{Lang.gallery.headline.moreGalleries}</span>
                    </NavigationGroup>
                    {isAuthorized && isOwner && <NavigationGroup>
                        <Tooltip className=' btn btn-overrides'
                                 title={Lang.gallery.headline.newGallery}>
                            <Popover className='popover-status-container'
                                     placement='bottom'
                                     render={handleGalleryPopover}>
                                <button className='btn rounded-button-regular rounded-button btn-outline-light'>
                                    <Add/>
                                </button>
                            </Popover>
                        </Tooltip>

                    </NavigationGroup>}
                </NavigationRow>
                {gallerySpaces && gallerySpaces.length > 0 && renderGallerySpaces()}
            </div>

            {showMediaGallery.isOpen &&
                <LightboxGallery media={mediaUrls.current}
                                 ref={mediaGalleryRef}
                                 open={true} index={showMediaGallery.index}
                                 onClose={() => handleShowMediaGallery(false, 0)}/>
            }
            <div className="widget-container pt-4">
                {renderBottomWidgets(widgets, authorization)}
            </div>
        </div>
    );
}

export default HeadlinesHome;